import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getArtists, getAllLocations } from '../../services/artistService';
import { getCurrentUser } from '../../services/authService';
import Combobox from '../../components/common/Combobox';
import Spinner from '../../components/common/Spinner';

import Button from '../../components/common/Button';
import instrumentsData from '../../data/instrumentsData';
import genresData from '../../data/genresData';
import professionsData from '../../data/professionsData';
import './ArtistsList.css';

/**
 * ArtistsList component for displaying and searching for artists
 */
const ArtistsList = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [, setUser] = useState(null);
  const [, setTotalArtists] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(12); // Increased since we no longer have side-by-side layout
  // Filter options
  const [instruments, setInstruments] = useState([]);
  const [genres, setGenres] = useState([]);
  const [locations, setLocations] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [, setLoadingOptions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    instrument: '',
    genre: '',
    location: '',
    profession: '',
    maxRate: ''
  });

  useEffect(() => {
    const init = async () => {
      try {
        // Get current user
        const { user: currentUser } = await getCurrentUser();
        setUser(currentUser);
        
        // Load filter options
        await loadFilterOptions();
        
        // Fetch artists
        fetchArtists();
      } catch (err) {
        console.error('Error initializing artists page:', err);
      }
    };
    
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const loadFilterOptions = async () => {
    try {
      setLoadingOptions(true);
      
      // Use static data for instruments, genres, and professions
      setInstruments(instrumentsData);
      setGenres(genresData);
      setProfessions(professionsData);
      
      // Load locations from database since they're dynamic
      const { locations: locationsList } = await getAllLocations();
      setLocations(locationsList || []);
      
      console.log('Filter options loaded:', {
        instruments: instrumentsData.length,
        genres: genresData.length,
        professions: professionsData.length,
        locations: locationsList?.length || 0
      });
    } catch (err) {
      console.error('Error loading filter options:', err);
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchArtists = async (page = currentPage) => {
    try {
      setLoading(true);
      console.log('Fetching artists for page', page, 'with filters:', filters);
      
      // Use the artistService to get artists with filters
      const { artists: artistsData, count, totalPages: pages, error: artistsError } = 
        await getArtists(filters, page, pageSize);
      
      console.log('Artists data received:', artistsData);
      console.log('Total count:', count);
      
      if (artistsError) {
        console.error('Error from artistService:', artistsError);
        throw artistsError;
      }
      
      setArtists(artistsData);
      setTotalArtists(count || 0);
      setTotalPages(pages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching artists:', error.message);
      console.error('Error details:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchArtists(newPage);
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const countActiveFilters = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  return (
    <div className="search-container">
      {/* Simple Artists title */}
      <h1 className="artists-title">Artists</h1>
      
      {/* Search Bar */}
      <div className="search-actions-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input 
              type="text" 
              placeholder="Search" 
              onChange={(e) => {
                // This would be expanded in a real implementation to handle search
                console.log('Search term:', e.target.value);
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Filter Toggle Button */}
      <div className="filter-toggle-container">
        <Button 
          variant="neutral"
          className={showFilters ? 'active' : ''}
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
        
        {/* Display active filter count */}
        {countActiveFilters() > 0 && (
          <span className="active-filter-count">
            {countActiveFilters()} active filter(s)
          </span>
        )}
      </div>
      
      {/* Filter Panel */}
      {showFilters && (
        <div className="filter-panel">
          <div className="filter-row">
            {/* Instrument Filter */}
            <div className="filter-item">
              <label htmlFor="instrument-filter">Instrument</label>
              <Combobox
                id="instrument-filter"
                options={instruments}
                value={filters.instrument}
                placeholder="Select instrument"
                onChange={(value) => {
                  setFilters({...filters, instrument: value});
                  console.log('Instrument filter changed to:', value);
                }}
              />
            </div>
            
            {/* Genre Filter */}
            <div className="filter-item">
              <label htmlFor="genre-filter">Genre</label>
              <Combobox
                id="genre-filter"
                options={genres}
                value={filters.genre}
                placeholder="Select genre"
                onChange={(value) => {
                  setFilters({...filters, genre: value});
                  console.log('Genre filter changed to:', value);
                }}
              />
            </div>
          </div>
          
          <div className="filter-row">
            {/* Location Filter */}
            <div className="filter-item">
              <label htmlFor="location-filter">Location</label>
              <Combobox
                id="location-filter"
                options={locations}
                value={filters.location}
                placeholder="Select location"
                onChange={(value) => {
                  setFilters({...filters, location: value});
                  console.log('Location filter changed to:', value);
                }}
              />
            </div>
            
            {/* Profession Filter */}
            <div className="filter-item">
              <label htmlFor="profession-filter">Profession</label>
              <Combobox
                id="profession-filter"
                options={professions}
                value={filters.profession}
                placeholder="Select profession"
                onChange={(value) => {
                  setFilters({...filters, profession: value});
                  console.log('Profession filter changed to:', value);
                }}
              />
            </div>
          </div>
          
          <div className="filter-actions">
            <Button 
              variant="primary"
              onClick={() => {
                fetchArtists(1);
              }}
            >
              Apply Filters
            </Button>
            
            <Button 
              variant="neutral"
              onClick={() => {
                setFilters({
                  instrument: '',
                  genre: '',
                  location: '',
                  profession: '',
                  maxRate: ''
                });
                setTimeout(() => fetchArtists(1), 0);
              }}
            >
              Reset Filters
            </Button>
          </div>
        </div>
      )}
      
      {/* Artists Content - Side by Side Layout */}
      {loading ? (
        <div className="my-5">
          <Spinner message="Loading artists..." fullPage={true} />
        </div>
      ) : (
        <div className="side-by-side-container">
          {/* Left side: Listings */}
          <div className="listings-container">
            {artists.length > 0 ? (
              <div className="artist-cards">
                {artists.map(artist => (
                  <div className="artist-card" key={artist.id || artist.uuid}>
                    <div className="artist-info">
                      <div>
                        {artist.profile_image_url ? (
                          <img 
                            src={artist.profile_image_url} 
                            alt={artist.full_name} 
                            className="artist-profile-image"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/80';
                            }}
                          />
                        ) : (
                          <div className="artist-profile-placeholder">
                            {artist.full_name ? artist.full_name.charAt(0).toUpperCase() : '?'}
                          </div>
                        )}
                      </div>
                      
                      <div className="artist-details">
                        <h2 className="artist-name">{artist.full_name}</h2>
                        
                        {/* Primary Profession */}
                        <p className="artist-profession">
                          {artist.profession || 
                            (artist.professions && artist.professions.length > 0 
                              ? artist.professions[0] 
                              : artist.instruments && artist.instruments.length > 0 
                                ? artist.instruments[0] 
                                : 'Musician')}
                        </p>
                        
                        <div className="artist-metadata">
                          {/* Professions */}
                          {artist.professions && artist.professions.length > 0 && (
                            <div className="metadata-item metadata-item-full">
                              <div className="metadata-label">Profession(s)</div>
                              <div className="metadata-value">
                                {artist.professions.join(', ')}
                              </div>
                            </div>
                          )}
                          
                          {/* Instruments */}
                          {artist.instruments && artist.instruments.length > 0 && (
                            <div className="metadata-item metadata-item-full">
                              <div className="metadata-label">Instrument(s)</div>
                              <div className="metadata-value">
                                {artist.instruments.join(', ')}
                              </div>
                            </div>
                          )}
                          
                          {/* Genres */}
                          {artist.genres && artist.genres.length > 0 && (
                            <div className="metadata-item metadata-item-full">
                              <div className="metadata-label">Genre(s)</div>
                              <div className="metadata-value">
                                {artist.genres.join(', ')}
                              </div>
                            </div>
                          )}
                          
                          {/* View Details Button */}
                          <div className="metadata-item metadata-item-full view-details-container">
                            <Link 
                              to={`/artists/${artist.id}`} 
                              className="view-details-button"
                            >
                              View Details
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="filter-button"
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Show first page, last page, current page, and pages around current
                        return page === 1 || 
                               page === totalPages || 
                               (page >= currentPage - 1 && page <= currentPage + 1);
                      })
                      .map((page, index, array) => {
                        // Add ellipsis if there are gaps
                        const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1;
                        const showEllipsisAfter = index < array.length - 1 && array[index + 1] !== page + 1;
                        
                        return (
                          <React.Fragment key={page}>
                            {showEllipsisBefore && <span>...</span>}
                            
                            <button 
                              onClick={() => handlePageChange(page)}
                              className={`filter-button ${currentPage === page ? 'active' : ''}`}
                            >
                              {page}
                            </button>
                            
                            {showEllipsisAfter && <span>...</span>}
                          </React.Fragment>
                        );
                      })}
                    
                    <button 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="filter-button"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center my-5">
                <p>No artists found matching your criteria.</p>
                <button 
                  className="filter-button"
                  onClick={() => {
                    setFilters({
                      instrument: '',
                      genre: '',
                      location: '',
                      minExperience: '',
                      maxRate: ''
                    });
                    setTimeout(() => fetchArtists(1), 0);
                  }}
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
          {/* Map removed as requested */}
        </div>
      )}
    </div>
  );
};

export default ArtistsList;
